import * as EdgeType from "utils/EdgeType";
import * as VideoReadyState from "utils/VideoReadyState";
import $ from "zeptojs";

import clone from "lodash/clone";
import noop from "lodash/noop";
import uniqueId from "lodash/uniqueId";
import omit from "lodash/omit";
import pick from "lodash/pick";
import isFunction from "lodash/isFunction";
import defer from "lodash/defer";
import delay from "lodash/delay";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import wrap from "lodash/wrap";

import checkEdge from "utils/checkEdge";
import classnames from "classnames";
import cutPosterImage from "utils/video/cutPosterImage";
import DefunctVideoComponentError from "components/errors/DefunctVideoComponentError";
import Device from "utils/Device";
import Emitter from "node-event-emitter";
import findBufferedTimeRemaining from "utils/findBufferedTimeRemaining";
import onUserInteractionInitiatedPlaybackPropType from "utils/onUserInteractionInitiatedPlaybackPropType";
import Promise from "bluebird";
import PropTypes from "prop-types";
import React from "react";
import ReactDOM from "react-dom";
import sendErrorReport from "utils/sendErrorReport";
import videojs from "video.js";
import { autobind } from "core-decorators";
import { VerseImaEvent } from "utils/videoPlugins/verseImaAds";
import canAutoPlay from "can-autoplay";
import MediaContext from "context/MediaContext";

// Fix WebVTT global
import vtt, { WebVTT, VTTCue } from "videojs-vtt.js";
import {
  registerTextTrackSettings,
  videoPlayerLoaded,
} from "./TextTrackSettings";
import styles from "./VideoComponent.css";

window.vttjs = vtt; // Fix chrome
window.VTTCue = VTTCue; // IE expects this to be global
window.WebVTT = WebVTT; // videojs expects this to be global

// How much time has to pass without a `timeupdate` before we decide
// that the media container is stalled due to lack of data.
const STALL_TIMEOUT_MILLISECONDS = 500;
const SPINNER_TIMEOUT_MS = 3000;

const XHR_ERROR_SEGMENT_DIFF = 2000; // roughly a segment
let hasDoneAutoplayCheck = false; // global flag

registerTextTrackSettings();

export default class VideoComponent extends React.Component {
  static contextType = MediaContext;

  static propTypes = {
    active: PropTypes.bool.isRequired,
    adNetworkOptions: PropTypes.object,
    adNetworkUrl: PropTypes.string,
    adPlaying: PropTypes.bool.isRequired,
    adRequiresDirectUserInteraction: PropTypes.bool.isRequired,
    autoplay: PropTypes.bool.isRequired,
    canAutoplay: PropTypes.bool.isRequired,
    canAutoplaySilentVideo: PropTypes.bool.isRequired,
    canPlayAds: PropTypes.bool.isRequired,
    canPlaySkippableAds: PropTypes.bool.isRequired,
    dataAttributes: PropTypes.string,
    debugVideoPlayback: PropTypes.bool,
    destroyPlaybackStateOnUnmount: PropTypes.bool,
    forceMute: PropTypes.bool,
    height: PropTypes.number,
    hide: PropTypes.bool,
    inactive: PropTypes.bool,
    initialDashBitrate: PropTypes.number.isRequired,
    is360: PropTypes.bool.isRequired,
    limitBitrateByPortal: PropTypes.bool.isRequired,
    loop: PropTypes.bool,
    mediaSource: PropTypes.arrayOf(PropTypes.object).isRequired,
    muted: PropTypes.bool.isRequired,
    needsThreeLoadReset: PropTypes.bool.isRequired,
    notifyInitialVideoCanPlayThroughReceived: PropTypes.func.isRequired,
    onAdComplete: PropTypes.func.isRequired,
    onAdIsSkipAllowedNowChange: PropTypes.func.isRequired,
    onAdIsSkippableChange: PropTypes.func.isRequired,
    onAdLoaded: PropTypes.func.isRequired,
    onAdNotPlaying: PropTypes.func.isRequired,
    onAdPaused: PropTypes.func.isRequired,
    onAdPlaying: PropTypes.func.isRequired,
    onAdResumed: PropTypes.func.isRequired,
    onCutPosterImage: PropTypes.func.isRequired,
    onHotspotClicked: PropTypes.func.isRequired,
    onHotspotOffered: PropTypes.func.isRequired,
    onInitiallyStalled: PropTypes.func.isRequired,
    onOrientationUpdated: PropTypes.func.isRequired,
    onPlaying: PropTypes.func,
    onSetAdDuration: PropTypes.func.isRequired,
    pauseVideoOnBlur: PropTypes.bool.isRequired,
    pixelRatio: PropTypes.number.isRequired,
    playAd: PropTypes.bool.isRequired,
    playbackRate: PropTypes.number.isRequired,
    playing: PropTypes.bool.isRequired,
    playingPassive: PropTypes.bool.isRequired,
    posterUrl: PropTypes.string.isRequired,
    receivedUserInteraction: PropTypes.bool.isRequired,
    requireVerseThree: PropTypes.bool.isRequired,
    scrubbing: PropTypes.bool.isRequired,
    seeking: PropTypes.bool.isRequired,
    showVideoPosters: PropTypes.bool.isRequired,
    showingChapterCover: PropTypes.bool.isRequired,
    supportsPosterImageCapture: PropTypes.bool.isRequired,
    textTrackCaptionSettingsVisible: PropTypes.bool,
    textTrackLanguage: PropTypes.string,
    textTracks: PropTypes.array,
    threeConfig: PropTypes.object,
    time: PropTypes.number.isRequired,
    timeOffset: PropTypes.number,
    usePersistentVideo: PropTypes.bool.isRequired,
    videoId: PropTypes.number.isRequired,
    waitForDataLoadedToSeek: PropTypes.bool.isRequired,
    waitForDurationChangeToSeek: PropTypes.bool.isRequired,
    waitingForInitialPlay: PropTypes.bool.isRequired,
    width: PropTypes.number,
    windowIsBlurred: PropTypes.bool.isRequired,

    handleNotReady: PropTypes.func,
    userInitiatedPause: PropTypes.bool.isRequired,

    onDurationChange: PropTypes.func,
    onEnded: PropTypes.func,
    onLoadStart: PropTypes.func.isRequired,
    onNotStalled: PropTypes.func,
    onPause: PropTypes.func,
    onPlay: PropTypes.func,
    onReady: PropTypes.func,
    onSeek: PropTypes.func,
    onSeeked: PropTypes.func,
    onSeeking: PropTypes.func,
    onStalled: PropTypes.func,
    onTextTrackSettingsClosed: PropTypes.func,
    onTimeUpdate: PropTypes.func,
    onUserInteractionInitiatedPlayback:
      onUserInteractionInitiatedPlaybackPropType,
    /* eslint-enable react/sort-prop-types */
  };

  static defaultProps = {
    dataAttributes: null,
    forceMute: false,
    height: 1080,
    isSubchapter: false,
    loop: false,
    onPlaying: noop,
    onTextTrackSettingsClosed: noop,
    onUserInteractionInitiatedPlayback: noop,
    showingChapterCover: false,
    timeOffset: 0,
    updateBuffer: true,
    width: 1920,
  };

  containerRef = React.createRef();

  primaryMediaNodeRef =
    this.constructor.savedPrimaryMediaNodeRef || React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      actuallyPlaying: false,
      allowSpinner: false,
      bufferPrimary: [],
      inactive: false,
      playbackFps: 0,
      primaryNodeStalled: false,
      readyState: 0,
      seeking: true,
      videoHeight: null,
      videoId: uniqueId("verse-video-"),
      videoWidth: null,
    };

    if (DEVELOPMENT) {
      // Create variables for debugging framerate on canvas video
      // player
      this.videoSeekDurationLog = [];
      this.renderDurationLog = [];

      this.lastDrawTimestamp = 0;
    }
  }

  UNSAFE_componentWillMount() {
    // Initialize `playerEvents` so even if we don't add any, the
    // variable is not `undefined.
    this.playerEvents = [];

    this.playWhenReady = false;
    this.readyToPlay = false;

    if (this.props.requireVerseThree && !window.VerseThree) {
      import(/* webpackChunkName: "verse-three" */ "verse-three").then(
        (verseThree) => {
          window.VerseThree = verseThree;
        },
      );
    }
  }

  componentDidMount() {
    if (this.props.active) {
      this.takeControlOfVideoPlayer();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const timeDiff = Math.abs(nextProps.time - this.props.time);
    const noLongerScrubbing = checkEdge(
      this.props,
      nextProps,
      "scrubbing",
      EdgeType.NEGATIVE,
    );
    if (
      this.props.active &&
      ((timeDiff > 2000 && !noLongerScrubbing) ||
        (this.props.scrubbing && timeDiff > 100) ||
        noLongerScrubbing)
    ) {
      this.seek({ value: nextProps.time });
    }

    if (checkEdge(this.props, nextProps, "active", EdgeType.NEGATIVE)) {
      this.cutPosterImage();
    }

    if (this.props.playing === false && nextProps.playing === true) {
      // Playing changed to `true`.

      // Bandaid: Play if we're active, pause if we're not. This is due to a bug
      // in our code that causes this video to play when inactive on Big Sur
      // Safari.
      if (nextProps.active) {
        this.play();
      } else {
        this.pause();
      }
    } else if (
      checkEdge(this.props, nextProps, "playingPassive", EdgeType.POSITIVE)
    ) {
      this.play();
    } else if (checkEdge(this.props, nextProps, "playing", EdgeType.NEGATIVE)) {
      this.pause();
    } else if (
      checkEdge(this.props, nextProps, "playingPassive", EdgeType.NEGATIVE)
    ) {
      this.pause();
    }

    if (this.props.active && !nextProps.active) {
      this.detachThreeScene();
    }
  }

  shouldComponentUpdate(props, state) {
    let hasStateChange = false;
    let hasPropsChange = false;

    for (const key in omit(props, ["time", "threeConfig"])) {
      if (props[key] !== this.props[key]) {
        hasPropsChange = true;
        break;
      }
    }

    for (const key in omit(state, ["bufferPrimary"])) {
      if (state[key] !== this.state[key]) {
        hasStateChange = true;
        break;
      }
    }

    if (this.state.primaryNodeStalled) {
      for (const key in pick(state, ["bufferPrimary"])) {
        if (state[key] !== this.state[key]) {
          hasStateChange = true;
          break;
        }
      }
    }

    return hasStateChange || hasPropsChange;
  }

  componentDidUpdate(oldProps, oldState) {
    this.handleComponentDidUpdateShowVideoPosters(
      oldProps.showVideoPosters,
      this.props.showVideoPosters,
    );
    this.handleComponentDidUpdateWindowIsBlurred(
      oldProps.windowIsBlurred,
      this.props.windowIsBlurred,
    );

    const becameActive = this.props.active && !oldProps.active;
    const becameInactive = !this.props.active && oldProps.active;

    if (this.props.usePersistentVideo) {
      if (becameActive) {
        this.takeControlOfVideoPlayer();
      } else if (becameInactive) {
        this.detachPlayerEvents();
      }
    } else if (becameActive && this.props.canAutoplay) {
      this.takeControlOfVideoPlayer();
    }

    this.updatedMuted();

    if (oldProps.playbackRate !== this.props.playbackRate) {
      this.videoPlayer.playbackRate(this.props.playbackRate);
    }

    const effectivePrimaryNodeStalledNow = this.state.primaryNodeStalled;
    const effectivePrimaryNodeStalledBeforeUpdate = oldState.primaryNodeStalled;

    const stalledNow = effectivePrimaryNodeStalledNow;
    const stalledBeforeUpdate = effectivePrimaryNodeStalledBeforeUpdate;

    if (stalledNow !== stalledBeforeUpdate) {
      // Stall state has change

      if (stalledNow) {
        if (isFunction(this.props.onStalled)) {
          this.props.onStalled();
        }
      } else if (isFunction(this.props.onNotStalled)) {
        this.props.onNotStalled();
      }
    }

    if (this.videoPlayer) {
      // If this video is active AND we hit a negative edge on scrubbing AND the user scrubbed to
      // the end of the video, then fire the `onEnded` callback.
      const isScrubbing = this.props.scrubbing;
      const wasScrubbing = oldProps.scrubbing;
      const durationSeconds = this.videoPlayer.duration();
      const timeWithOffsetSeconds =
        this.props.time / 1000 + this.props.timeOffset / 1000;
      if (this.props.active && !isScrubbing && wasScrubbing) {
        if (timeWithOffsetSeconds >= durationSeconds && durationSeconds > 0) {
          defer(this.props.onEnded);
        }
      }
    }

    const activelyReceivedUserInteraction = checkEdge(
      oldProps,
      this.props,
      "receivedUserInteraction",
      EdgeType.POSITIVE,
    );
    const adNoLongerRequiresInteraction = checkEdge(
      oldProps,
      this.props,
      "adRequiresDirectUserInteraction",
      EdgeType.NEGATIVE,
    );

    if (
      this.props.active &&
      (activelyReceivedUserInteraction || adNoLongerRequiresInteraction)
    ) {
      // console.log("NO LONGER WAITING");
      this.handleNoLongerWaitingForReceivedUserInteraction();
    }

    if (
      this.props.active &&
      checkEdge(oldProps, this.props, "userRequestedAdSkip", EdgeType.POSITIVE)
    ) {
      this.videoPlayer.trigger(VerseImaEvent.USER_REQUESTED_SKIP);
    }

    if (this.props.textTrackLanguage !== oldProps.textTrackLanguage) {
      this.showTextTrackForLanguage(this.props.textTrackLanguage);
    }

    if (
      checkEdge(
        oldProps,
        this.props,
        "textTrackCaptionSettingsVisible",
        EdgeType.POSITIVE,
      ) &&
      this.props.active
    ) {
      this.showTextTrackSettings();
    } else if (
      checkEdge(
        oldProps,
        this.props,
        "textTrackCaptionSettingsVisible",
        EdgeType.NEGATIVE,
      )
    ) {
      this.hideTextTrackSettings();
    }

    const propsToOmitInThreeUpdate = ["initialRotation"];
    if (
      !isEqual(
        omit(oldProps.threeConfig, propsToOmitInThreeUpdate),
        omit(this.props.threeConfig, propsToOmitInThreeUpdate),
      )
    ) {
      // config changed. recycle the scene
      this.detachThreeScene();
      this.attachThreeScene();
    }
  }

  componentWillUnmount() {
    this.isUnmounting = true;

    this.detachPlayerEvents();

    if (this.spinnerWaitTimer) {
      window.clearTimeout(this.spinnerWaitTimer);
    }

    if (!this.props.usePersistentVideo && this.videoPlayer) {
      // Dispose of player.
      this.videoPlayer.dispose();
      this.videoPlayer = null;
    }

    this.detachThreeScene();

    window.clearTimeout(this.primaryNodeStallTimeout);
  }

  cutPosterImage() {
    if (this.props.supportsPosterImageCapture) {
      try {
        const canCut =
          !this.waitingForReady && this.waitingForReady !== undefined;
        if (canCut) {
          const videoEl = $(this.videoPlayer.el()).find("video")[0];
          const imgDat = cutPosterImage(videoEl);
          this.props.onCutPosterImage(imgDat);
        }
      } catch {
        console.warn("Unable to cut the poster image from the video frame"); // eslint-disable-line no-console
      }
    }
  }

  @autobind
  doAutoplayCheck() {
    if (!hasDoneAutoplayCheck) {
      hasDoneAutoplayCheck = true; // globally
      canAutoPlay
        .video({ timeout: 1500, muted: true, inline: true })
        .then(({ result }) => {
          if (!result) {
            this.handlePause();
            this.props.onInitiallyStalled(); // video player stalled out on initial play
          }
        });
    }
  }

  handleNoLongerWaitingForReceivedUserInteraction() {
    if (!this.videoPlayer) {
      return;
    }

    const videoPlayerElement = $(this.videoPlayer.el())
      .find(this.nodeType)
      .get(0);
    videoPlayerElement.removeAttribute("autoplay");
    videoPlayerElement.pause();
    this.videoPlayer.autoplay(true);

    const eventsSignifyingPlayback = [
      "playing",
      "timeupdate",
      VerseImaEvent.AD_PLAYING,
    ];

    const eventsSignifyingAdComplete = [VerseImaEvent.AD_COMPLETE];

    const handleAdComplete = () => {
      eventsSignifyingAdComplete.forEach((eventName) => {
        this.videoPlayer.off(eventName, handleAdComplete);
      });
      this.props.onUserInteractionInitiatedPlayback();
    };

    const callback = () => {
      eventsSignifyingPlayback.forEach((eventName) => {
        this.videoPlayer.off(eventName, callback);
      });

      if (!this.props.playAd || !this.props.adRequiresDirectUserInteraction) {
        // There is no ad or we don't need direct user interaction
        // with the ad. Call the callback now.
        this.props.onUserInteractionInitiatedPlayback();
      } else {
        eventsSignifyingAdComplete.forEach((eventName) => {
          this.videoPlayer.on(eventName, handleAdComplete);
        });
      }
    };

    eventsSignifyingPlayback.forEach((eventName) => {
      this.videoPlayer.on(eventName, callback);
    });

    if (this.videoPlayer.verseAds) {
      this.videoPlayer.verseAds.triggerUserInteraction();
    }
    this.videoPlayer.play();
  }

  handleComponentDidUpdateShowVideoPosters(
    wasShowingMoviePosters,
    isShowingMoviePosters,
  ) {
    // If we were showing movie posters and now aren't, then remove them for the entire duration
    // of this video's lifecycle.
    const negativeEdge = wasShowingMoviePosters && !isShowingMoviePosters;

    if (!this.videoPlayer || !this.props.active || !negativeEdge) {
      return;
    }

    const $targetElement = $($(this.videoPlayer.el()).find("video"));
    $targetElement.removeAttr("poster");
  }

  handleComponentDidUpdateWindowIsBlurred(
    wasShowingMoviePosters,
    isShowingMoviePosters,
  ) {
    // If we were showing movie posters and now aren't, then remove them for the entire duration
    // of this video's lifecycle.
    const positiveEdge = !wasShowingMoviePosters && isShowingMoviePosters;

    if (!this.videoPlayer || !this.props.active || !positiveEdge) {
      return;
    }

    if (this.props.pauseVideoOnBlur) {
      this.pause();
    }
  }

  checkWaitingSpinner() {
    if (!this.videoPlayer || !this.videoPlayer.el() || this.isDefunctPlayer) {
      return;
    }

    // Do nothing if we're stalled.
    if (this.state.primaryNodeStalled) {
      return;
    }

    // Do nothing if we're not playing.
    if (!this.props.playing) {
      return;
    }

    // Do nothing if we're seeking.
    if (this.props.seeking) {
      return;
    }

    try {
      const $node = $(this.videoPlayer.el());
      if ($node.hasClass("vjs-waiting")) {
        $node.removeClass("vjs-waiting");
      }
    } catch {
      // ignore
    }
  }

  get currentPrimaryTime() {
    return Math.round(
      this.videoPlayer.currentTime() * 1000 + this.props.timeOffset,
    );
  }

  @autobind
  seek({ value }) {
    if (this.videoPlayer) {
      const targetTime = (value - this.props.timeOffset) / 1000;

      this.resetSpinnerWaitTimer();
      // Set the video player time.
      this.videoPlayer.currentTime(targetTime);

      // If we are seeking to more than .5 seconds away from the end of the video, then we
      // guarantee `this.ended` is `false.`
      if (Math.abs(targetTime - this.videoPlayer.duration()) > 0.5) {
        this.ended = false;
      }

      // Temporarily set `stalled` to `true`. It will be set to
      // `false` after an `timeupdate` event is fired, meaning the
      // seek is complete.
      defer(() => {
        if (!this.isUnmounting) {
          this.setState({
            primaryNodeStalled: true,
          });
        }
      });

      const isGoingToSeek =
        Math.abs(this.videoPlayer.currentTime() - targetTime) > 0;
      this.setState({ seeking: isGoingToSeek });
    }
  }

  resetSpinnerWaitTimer() {
    if (this.unmounting) {
      return;
    }

    if (this.spinnerWaitTimer) {
      window.clearTimeout(this.spinnerWaitTimer);
    }
    this.setState({
      allowSpinner: false,
    });
    this.spinnerWaitTimer = setTimeout(() => {
      this.setState({
        allowSpinner: true,
      });
    }, SPINNER_TIMEOUT_MS);
  }

  debugVideoPlayerEvents(player, videoId) {
    if (this.props.debugVideoPlayback) {
      const events = [
        "abort",
        "canplay",
        "canplaythrough",
        "durationchange",
        "emptied",
        "ended",
        "ended",
        "error",
        "loadeddata",
        "loadedmetadata",
        "loadstart",
        "pause",
        "play",
        "playing",
        "progress",
        "ratechange",
        "seeked",
        "seeking",
        "stalled",
        "suspend",
        "timeupdate",
        "volumechange",
        "waiting",
        VerseImaEvent.AD_PLAYING,
      ];
      events.forEach((eName) => {
        this.attachSinglePlayerEvent(eName, () =>
          // eslint-disable-next-line no-console
          console.log(`[Video: ${videoId}] -> ${eName}`),
        );
      });
    }
  }

  @autobind
  handleVideoSeeked() {
    if (isFunction(this.props.onSeeked)) {
      this.props.onSeeked();
    }

    this.setState({ seeking: false });
  }

  @autobind
  handleVideoSeeking() {
    if (isFunction(this.props.onSeeking)) {
      this.props.onSeeking();
    }
  }

  showTextTrackForLanguage(languageKey) {
    // set all as hidden first
    if (this.videoPlayer) {
      const tracks = this.videoPlayer.remoteTextTracks().tracks_ || [];
      const lang = languageKey || ""; // sanitize undefined language key
      tracks.forEach((track) => {
        track.mode = "hidden";
        if (track.language === lang) {
          track.mode = "showing";
        }
      });
    }
  }

  showTextTrackSettings() {
    const trackSettings = this.videoPlayer.getChild("textTrackSettings");
    trackSettings.show(); // show the track settings
    const button = trackSettings.$(".vjs-done-button");
    const ttsBackground = trackSettings.$(".tts__bg");
    const callback = () => {
      this.props.onTextTrackSettingsClosed();
      trackSettings.off(button, "click", callback);
      trackSettings.off(ttsBackground, "click", callback);
    };
    trackSettings.on(button, "click", callback);
    trackSettings.on(ttsBackground, "click", callback);
  }

  hideTextTrackSettings() {
    if (this.videoPlayer) {
      const trackSettings = this.videoPlayer.getChild("textTrackSettings");
      trackSettings.hide(); // show the track settings
      this.props.onTextTrackSettingsClosed();
    }
  }

  replaceTextTracks(videoPlayer) {
    if (videoPlayer) {
      // Quickfix for VD-715
      // Clone tracks_ because the array is being manipulated during the call
      // to removeRemoteTextTrack and, as a result, the indexing gets messed up
      // and not all of the tracks are actually removed. Depending on the order
      // of the text tracks and the default/selected language, this can result
      // in one of the old, un-removed tracks being reused.
      // See: https://verseplayer.atlassian.net/browse/VD-715
      const remoteTextTracks = clone(videoPlayer.remoteTextTracks().tracks_);

      remoteTextTracks.forEach((track) => {
        videoPlayer.removeRemoteTextTrack(track);
      });

      (this.props.textTracks || []).forEach((track) => {
        videoPlayer.addRemoteTextTrack(track);
      });

      this.showTextTrackForLanguage(this.props.textTrackLanguage);
      if (this.props.textTrackCaptionSettingsVisible) {
        this.showTextTrackSettings();
      }
    }
  }

  updateVideoPlayer(videoPlayer) {
    this.debugVideoPlayerEvents(videoPlayer, this.props.videoId);
    videoPlayer.src(this.props.mediaSource);
    videoPlayer.videoId = this.props.videoId;

    this.replaceTextTracks(videoPlayer);

    if (this.props.showVideoPosters) {
      videoPlayer.poster(this.props.posterUrl);
    }

    videoPlayer.loop(
      !this.props.playAd && this.props.loop && !Device.isMobileSafari,
    );
    this.updatedMuted();
  }

  get firstVideoSourceUrl() {
    return this.props.mediaSource[0].src;
  }

  @autobind
  takeControlOfExistingPersistentVideoPlayer({ targetNode }) {
    const videoPlayer = this.constructor.persistentVideoPlayer;

    this.videoPlayer = videoPlayer;
    this.videoPlayer.pause();

    // Disatch event to notify that we are ceding control of the player.
    videoPlayer.customEmitter.emit("changing-control");
    videoPlayer.customEmitter.removeAllListeners("changing-control");

    // Attach the existing video player to the dom.
    targetNode.appendChild(videoPlayer.el());

    // Flag the player as waiting for it's initial signal
    // that the player is ready. This will prevent multiple
    // `canplay` events from calling
    // `this.props.onReady`.
    this.waitingForReady = true;

    // Update content.
    this.updateVideoPlayer(videoPlayer);

    // Set up ads after we've set the video content
    this.setupIma();

    this.context.setPlaybackRef(videoPlayer);

    this.context.setVideoRef(videoPlayer.el().querySelector("video"));

    // Don't autoplay if the device is Mobile
    if (!Device.isMobile && this.props.autoplay) {
      // Start playing
      this.play();
    } else if (!this.props.waitingForInitialPlay && this.props.autoplay) {
      this.play();
    }

    this.readyToPlay = false;
  }

  setupIma() {
    const imaConfig = {
      adNetworkUrl: this.props.adNetworkUrl,
      canPlayAds: this.props.canPlayAds,
      canPlaySkippableAds: this.props.canPlaySkippableAds,
      playAd: this.props.playAd,
      adNetworkOptions: this.props.adNetworkOptions,
      mediaTagName: this.nodeType,
      userInteractionRequiredBeforePlayback:
        this.props.adRequiresDirectUserInteraction,
      useVerseControlledMidroll: true,
      videoId: this.props.videoId,
    };

    const videoPlayer = this.videoPlayer;
    videoPlayer.verseImaAds(imaConfig);

    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_LOADED,
      this.props.onAdLoaded,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_PLAYING,
      this.props.onAdPlaying,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_PAUSED,
      this.props.onAdPaused,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_RESUMED,
      this.props.onAdResumed,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_NOT_PLAYING,
      this.props.onAdNotPlaying,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_TIME_UPDATE,
      this.handleTimeUpdate,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_COMPLETE,
      () => {
        this.resetSpinnerWaitTimer();
        this.props.onAdComplete();
        this.play();
      },
      videoPlayer,
    );

    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_DURATION_CHANGE,
      this.handleAdDurationChange,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_IS_SKIPPABLE_CHANGE,
      this.handleAdIsSkippableChange,
      videoPlayer,
    );
    this.attachSinglePlayerEvent(
      VerseImaEvent.AD_IS_SKIP_ALLOWED_NOW_CHANGE,
      this.handleAdIsSkipAllowedNowChange,
      videoPlayer,
    );
  }

  @autobind
  takeControlOfVideoPlayer() {
    // Get DOM target.
    const targetNode = this.containerRef.current;
    if (!this.constructor.videoPlayerControlPromiseChain) {
      this.constructor.videoPlayerControlPromiseChain = Promise.resolve();
    }

    this.constructor.videoPlayerControlPromiseChain =
      this.constructor.videoPlayerControlPromiseChain
        .then(
          this.takeControlOfVideoPlayerWorker.bind(this, {
            targetNode,
          }),
        )

        .then(this.videoPlayerReady)
        .then(() => this.attachThreeScene())

        .then(() => {
          const timeDiff = Math.abs(
            this.videoPlayer.currentTime() * 1000 - this.props.time,
          );
          // outside of the 1 second mark then seek to where it is supposed to be
          const outsideTimeRange = !!this.props.time && timeDiff > 1000;
          if (
            outsideTimeRange &&
            this.videoPlayer.duration() * 1000 > this.props.time
          ) {
            this.seek({ value: this.props.time });
          } else {
            this.setState({ seeking: false });
          }
        })

        // If multiple video players try to take control at once, video
        // players that became defunct will throw
        // `DefunctVideoComponentError`. We ignore them, their sole
        // purpose is to prevent any further video component actions
        // when the player knows it's not the most recent requester.
        .catch(DefunctVideoComponentError, noop)

        // Throw if there was an unexpected error.
        .catch((error) => {
          if (!this.isUnmounting) {
            // ignore errors from unmounting since the promise chain is async
            throw error;
          }
        });

    return this.constructor.videoPlayerControlPromiseChain;
  }

  // Node type that will be rendered for the primary media player.
  get nodeType() {
    return "video";
  }

  handlePrimaryMediaNodeRef = (ref) => {
    this.primaryMediaNodeRef.current = ref;

    if (this.nodeType === "video") {
      this.context.setVideoRef(ref);
    }
  };

  @autobind
  takeControlOfVideoPlayerWorker({ targetNode }) {
    const source = this.props.mediaSource;
    const loadingManifestPromise = Promise.resolve();
    return Promise.resolve()
      .then(
        () =>
          new Promise((resolve, reject) => {
            delay(() => {
              // 50ms delay has passed. If we are mounted, continue along
              // the promise chain. If we are not, then reject the
              // promise, therefore silently ignoring the request to take
              // control,
              if (!this.isUnmounting && this.props.active) {
                resolve();
              } else {
                reject(new DefunctVideoComponentError());
              }
            }, 50);
          }),
      )
      .then(() => {
        this.setState({ seeking: true });
      })
      .then(loadingManifestPromise)
      .then(
        () =>
          new Promise((resolve, reject) => {
            try {
              if (!this.props.usePersistentVideo && this.videoPlayer) {
                // We are not using persistent video and a video player
                // was already created. Everything is ready now, so just
                // resolve.

                return resolve();
              }
              if (
                this.props.usePersistentVideo &&
                this.constructor.persistentVideoPlayer
              ) {
                // There is already a persistent video player, use it.
                this.takeControlOfExistingPersistentVideoPlayer({
                  targetNode,
                  source,
                });

                // Resolve the promise.
                resolve();

                // Stop doing anything, we're done.
                return;
              }

              // Flag the player as waiting for it's initial signal that
              // the player is ready. This will prevent multiple `canplay`
              // events from calling `this.props.onReady`. This is not set
              // on `this.state` because it has nothing to do with the
              // DOM. Also, `this.setState` is asyncronous and we need
              // this to take effect instantly.
              this.waitingForReady = true;

              const el = React.createElement(this.nodeType, {
                className: classnames(
                  "video-js vjs-default-skin video-wrap__video-js",
                  styles.videoJs,
                  {
                    [styles.chromeFilterFix]:
                      Device.isChrome && Device.isDesktop,
                  },
                ),
                id: this.state.videoId,
                playsInline: true,
                "webkit-playsinline": "true",
                crossOrigin: "anonymous",
                ref: this.handlePrimaryMediaNodeRef,
              });

              ReactDOM.render(el, targetNode);

              // Setting the width and height for ios7 will cause the videos to not play
              const videoJSOptions = {
                autoplay: this.props.autoplay,
                controls: false,
                height: "100%",
                html5: {
                  dash: {
                    limitBitrateByPortal: this.props.limitBitrateByPortal,
                  },
                  nativeTextTracks: false,
                },
                persistTextTrackSettings: true,
                playsInline: true,
                starttime: (this.props.time - this.props.timeOffset) / 1000,
                volume: 1,
                muted: this.props.muted,
                width: "100%",
              };

              if (videojs.Html5DashJS) {
                const self = this;
                videojs.Html5DashJS.beforeInitialize = function (
                  player,
                  mediaPlayer,
                ) {
                  //mediaPlayer.getDebug().setLogToBrowserConsole(false);
                  mediaPlayer.on("error", (e) => {
                    if (e.error === "download") {
                      self.hadDashXHRError = true;
                    }
                  });

                  mediaPlayer.on("qualityChangeRendered", (e) => {
                    // console.log("qualityChanged", e);
                    // console.log(e.mediaType, JSON.stringify(mediaPlayer.getBitrateInfoListFor(e.mediaType)[e.newQuality]));
                    if (e.mediaType === "video") {
                      let newQuality = null;
                      let oldQuality = null;
                      if (
                        e.newQuality !== null &&
                        e.newQuality !== undefined &&
                        !isNaN(e.newQuality)
                      ) {
                        newQuality = mediaPlayer.getBitrateInfoListFor(
                          e.mediaType,
                        )[e.newQuality];
                      }
                      if (
                        e.oldQuality !== null &&
                        e.oldQuality !== undefined &&
                        !isNaN(e.oldQuality)
                      ) {
                        oldQuality = mediaPlayer.getBitrateInfoListFor(
                          e.mediaType,
                        )[e.oldQuality];
                      }
                      self.props.onBitrateChanged(oldQuality, newQuality);
                    }
                  });
                };
              }

              const videoPlayer = videojs(
                this.primaryMediaNodeRef.current,
                videoJSOptions,
              );

              // If `videojs` creates a _new_ video element, save that.
              if (videoPlayer.el().querySelector("video")) {
                this.handlePrimaryMediaNodeRef(
                  videoPlayer.el().querySelector("video"),
                );
              }

              this.context.setPlaybackRef(videoPlayer);

              if (this.props.showVideoPosters) {
                videoPlayer.poster(this.props.posterUrl);
              }

              videoPlayer.ready(() => {
                this.videoPlayer = videoPlayer;

                require("invariant")(
                  this.videoPlayer,
                  "videoplayer not defined",
                );

                // Set the newly created video player's source.
                this.updateVideoPlayer(videoPlayer);

                // Set up ads after we've set the video content
                this.setupIma();

                if (this.props.usePersistentVideo) {
                  this.attachPlayerEvents();
                }

                const isAutoplayMutedVideo =
                  this.props.forceMute && this.props.autoplay;
                if (isAutoplayMutedVideo) {
                  this.primaryMediaNodeRef.current.setAttribute(
                    "muted",
                    this.props.canAutoplaySilentVideo,
                  );

                  if (
                    this.props.canAutoplaySilentVideo &&
                    this.props.autoplay
                  ) {
                    this.primaryMediaNodeRef.current.setAttribute(
                      "autoplay",
                      true,
                    );
                  } else {
                    this.primaryMediaNodeRef.current.removeAttribute(
                      "autoplay",
                    );
                  }
                }

                if (this.props.usePersistentVideo) {
                  // If we're using a persistent video player, then save the
                  // reference.
                  this.constructor.savedPrimaryMediaNodeRef =
                    this.primaryMediaNodeRef;
                }

                // Set the dash options.
                this.setDashOptions(videoPlayer);

                // Create an emitter. This will be used to broadcast
                // when another `VideoComponent` is taking control
                // of the player.
                videoPlayer.customEmitter = new Emitter();

                if (DEVELOPMENT) {
                  window.videoPlayer = videoPlayer;
                }

                if (this.props.usePersistentVideo) {
                  this.constructor.persistentVideoPlayer = videoPlayer;
                }

                resolve();
              });
            } catch (error) {
              console.error("error!", error); //eslint-disable-line no-console
              reject(error);
            }
          }),
      );
  }

  attachThreeScene() {
    const videoEl = $(this.videoPlayer.el()).find("video").get(0);

    if (this.props.is360 && !this.verseThree) {
      this.verseThree = new window.VerseThree.VideoScene(
        videoEl,
        this.videoPlayer.el(),
        { insertBefore: true },
      );
      this.verseThree.setHandlers({
        onHotspotClicked: this.props.onHotspotClicked,
        onInitialOrientationUpdated: this.props.onOrientationUpdated,
        onHotspotOffered: this.props.onHotspotOffered,
      });
      this.verseThree.updateConfig(this.props.threeConfig);
      this.verseThree.start();
      if (this.props.needsThreeLoadReset && this.props.time > 0) {
        this.videoPlayer.load();
        this.seek(this.props.time);
      }
    }
  }

  detachThreeScene() {
    if (this.verseThree) {
      this.verseThree.destroy();
      this.verseThree = null;
    }
  }

  // Called when there is a working video player. This function is
  // responsible for setting up all the events on it. It returns a Promise
  // that resolves when the player is playing something.
  @autobind
  videoPlayerReady() {
    return new Promise((resolve) => {
      const videoPlayer = this.videoPlayer;

      // tell the text track settings
      videoPlayerLoaded();

      this.setDashOptions(videoPlayer);

      // Listen for when someone else takes control of the player so we
      // can prevent calling `this.props.onReady` for defunct players.
      // We must also save that this is *not* a defunct player because it
      // could have become defunct and then regained control
      this.isDefunctPlayer = false;
      videoPlayer.customEmitter.addListener("changing-control", () => {
        this.isDefunctPlayer = true;

        this.detachPlayerEvents();

        if (isFunction(this.props.handleNotReady)) {
          this.props.handleNotReady();
        }
      });

      this.updatedMuted();

      this.attachPlayerEvents();
      if (
        (this.props.playingPassive || this.props.canAutoplay) &&
        this.props.autoplay
      ) {
        this.play();

        this.videoPlayer.one("timeupdate", resolve);
      } else {
        resolve();
      }
    });
  }

  setDashOptions(videoPlayer) {
    const tech = videoPlayer.tech({ IWillNotUseThisInPlugins: true });
    if (tech.sourceHandler_ && tech.sourceHandler_.mediaPlayer_) {
      const mediaPlayer = tech.sourceHandler_.mediaPlayer_;
      mediaPlayer.setABRStrategy("abrDynamic"); // abrBola === buffer; abrThroughput === throughput history; abrDynamic === blend;
      // mediaPlayer.setLowLatencyEnabled(true);
      // mediaPlayer.setStableBufferTime(7);
      // mediaPlayer.setBandwidthSafetyFactor(.3);
      mediaPlayer.setFastSwitchEnabled(true);
      mediaPlayer.setUsePixelRatioInLimitBitrateByPortal(true);
      mediaPlayer.getDebug().setLogToBrowserConsole(false);
      this.hadDashXHRError = false;
    }
  }

  @autobind
  updatedMuted() {
    if (!this.props.active || !this.videoPlayer) {
      return;
    }

    const muted = this.props.muted;

    this.videoPlayer.volume(muted ? 0 : 1);
    this.videoPlayer.muted(muted);
  }

  @autobind
  play() {
    if (!this.props.active) {
      return; // don't play if inactive
    }
    if (!this.props.adPlaying && this.props.playAd) {
      return;
    }
    // Make desktops wait for 'canplay' before trying to play. This
    // matters because desktops download content immediately, but
    // tablets and phones wait until the user decides to play before
    // downloading.
    if (Device.isDesktop && !this.readyToPlay) {
      this.playWhenReady = true;
    } else {
      this.playWhenReady = false;
    }

    if (!this.videoPlayer) {
      return;
    }

    // Determine if we need to block the `play` command because the
    // video is over. This will be `true` if we're at the end of a video
    // and we *aren't* on Mobile Safari in a looping video (Mobile
    // Safari ignores loops in `<video>` elements so we need to call
    // `.play()` again to make it start over).
    const atEnd =
      this.videoPlayer.currentTime() === this.videoPlayer.duration() &&
      this.videoPlayer.duration() > 0;
    const forceLoop = this.props.loop && Device.isMobileSafari;
    const blockPlaybackBecauseItsOver =
      atEnd && !forceLoop && !this.props.playAd;

    if (!blockPlaybackBecauseItsOver) {
      this.videoPlayer.play();
    }

    this.resetSpinnerWaitTimer();
  }

  @autobind
  pause() {
    this.playWhenReady = false;
    if (!this.videoPlayer) {
      return;
    }

    // If events are still firing somehow for a video that is about to be inactive and
    // the player is persistent then check for pausing out of context
    const pausingVideoOutOfContext =
      this.props.usePersistentVideo &&
      this.videoPlayer.videoId !== this.props.videoId;
    if (pausingVideoOutOfContext) {
      return;
    }

    if (!this.videoPlayer.paused()) {
      this.videoPlayer.pause();
    }
  }

  @autobind
  attachSinglePlayerEvent(event, callback, target) {
    this.playerEvents = this.playerEvents || [];

    // Set the default target.
    target = target || this.videoPlayer;

    const wrappedCallback = wrap(callback, (func, ...args) => {
      func.call(this, ...args);
    });

    if (!target.on) {
      target = $(target);
    }

    this.playerEvents.push({
      callback: wrappedCallback,
      event,
      target,
    });

    target.on(event, wrappedCallback);
  }

  getIsUsingDash() {
    return this.props.mediaSource[0].type === "application/dash+xml";
  }

  @autobind
  onEnded() {
    if (this.props.loop) {
      // Force special loop behavior for mobile safari. For all other
      // browsers, do nothing.
      if (Device.isMobileSafari) {
        // Call `videoPlayer.play()` directly. This will bypass the
        // default behavior of only calling `play` if the player
        // is paused found in `this.play()`.
        this.videoPlayer.play();
      }
    } else if (isFunction(this.props.onEnded)) {
      // Flag this video as over so we stop trying to seek. iOS will set the time back to 0
      // automatically and that will cause a video stutter.
      this.ended = true;

      // Call `this.props.onEnded` if it exists and we aren't looping.
      this.props.onEnded();
    }
  }

  @autobind
  handleTimeUpdate(event) {
    if (this.props.adPlaying && event.type !== VerseImaEvent.AD_TIME_UPDATE) {
      // console.debug(`ignoring ${event.type} because ad is playing`);
      return;
    }
    if (!this.props.adPlaying && event.type.toLowerCase() !== "timeupdate") {
      // console.debug(`ignoring ${event.type} because ad isn't playing`);
      return;
    }

    if (this.ended) {
      return;
    }
    const timeMilliseconds =
      get(event, "detail.time", this.videoPlayer.currentTime()) * 1000;
    if (timeMilliseconds === this.previousPrimaryNodeTimeMilliseconds) {
      const duration = this.videoPlayer.duration() * 1000;
      const timeToEnd = Math.abs(timeMilliseconds - duration);
      if (
        duration > 0 &&
        timeToEnd < XHR_ERROR_SEGMENT_DIFF &&
        this.hadDashXHRError
      ) {
        // if there is a dash error and we are stalling under the last few seconds of the video then
        // assume that we did not transcode properly and timed out.
        this.onEnded();
      }
      if (
        duration > 0 &&
        timeToEnd < XHR_ERROR_SEGMENT_DIFF &&
        this.state.primaryNodeStalled
      ) {
        this.onEnded();
      }
      return;
    }
    this.previousPrimaryNodeTimeMilliseconds = timeMilliseconds;

    // Set a timeout to capture a stalled stream
    window.clearTimeout(this.primaryNodeStallTimeout);
    this.primaryNodeStallTimeout = window.setTimeout(
      this.handlePrimaryNodeStalled,
      STALL_TIMEOUT_MILLISECONDS,
    );

    // If we got a timeupdate, then the primary node is not stalled.
    this.setState({
      primaryNodeStalled: false,
    });

    this.checkWaitingSpinner();
    if (this.props.adPlaying) {
      this.resetSpinnerWaitTimer();
    }

    if (event.type === VerseImaEvent.AD_TIME_UPDATE || this.props.adPlaying) {
      this.dispatchAdTimeUpdate(timeMilliseconds);
    } else {
      this.dispatchTimeUpdate(timeMilliseconds);
    }
  }

  @autobind
  handleCanplay() {
    // Call `this.props.onReady` if:
    //
    // 1. The function exists
    //
    // 2. This is not a defunct player
    //
    // 3. `this.waitingForReady` is `false`. `this.waitingForReady`
    // prevents the player from calling `this.props.onReady`
    // more than once. `canplay` will fire each time we seek to a
    // new location that is not yet downloaded, so we must prevent
    // the handler being called repeatedly.
    if (!this.isDefunctPlayer && this.waitingForReady) {
      this.waitingForReady = false;

      if (isFunction(this.props.onReady)) {
        this.props.onReady();
      }
    }

    this.detachSinglePlayerEvent("canplay");

    this.readyToPlay = true;
    if (this.props.usePersistentVideo) {
      this.seek({ value: this.props.time });
      this.play();
    }

    if (this.playWhenReady) {
      this.play();
    }
  }

  @autobind
  attachBufferEvents(node, options) {
    const eventsToListenTo = [
      "canplay",
      "canplaythrough",
      "durationchange",
      "loadeddata",
      "loadedmetadata",
      "loadstart",
      "progress",
      "timeupdate",
    ];

    this.attachSinglePlayerEvent(
      "canplaythrough",
      this.props.notifyInitialVideoCanPlayThroughReceived,
    );

    // Update buffered playback progress on these events.
    eventsToListenTo.forEach((eventName) => {
      /* eslint-enable indent*/
      this.attachSinglePlayerEvent(
        eventName,
        (event) => {
          const target = event.target;
          let bufferNode;
          if (target.nodeName === "VIDEO") {
            bufferNode = target; // video node
          } else {
            bufferNode = $(target).find(this.nodeType).get(0); // primary node
          }

          const rawBuffered = get(bufferNode, "buffered", { length: 0 });

          const buffered = [];

          // `rawBuffered` can be `null`. Using `get()` will
          // prevent this from throwing an error.
          for (let i = 0; i < get(rawBuffered, "length"); i += 1) {
            buffered.push({
              start: Math.round(rawBuffered.start(i) * 1000),
              end: Math.round(rawBuffered.end(i) * 1000),
            });
          }

          // Update the state if the buffered amount has changed
          if (!isEqual(buffered, this.state[options.stateKey])) {
            this.setState({
              [options.stateKey]: buffered,
            });
          }
        },
        node,
      );
    }, this);
  }

  @autobind
  handleDurationChange(event) {
    const timeDiff =
      this.videoPlayer.currentTime() * 1000 -
      (this.props.time - this.props.timeOffset);
    const videoOutOfRange = Math.abs(timeDiff) > 1000;
    if (this.props.waitForDurationChangeToSeek && videoOutOfRange) {
      this.seek({ value: this.props.time });
    }
    if (this.props.playing && this.videoPlayer.paused()) {
      this.play();
    }
    const duration =
      get(event, "detail.duration", this.videoPlayer.duration()) * 1000;

    if (
      isFunction(this.props.onDurationChange) &&
      !this.isDefunctPlayer &&
      duration
    ) {
      this.props.onDurationChange(duration);
    }
  }

  @autobind
  handleAdDurationChange(event) {
    const duration = event.detail.duration * 1000;
    if (!this.isDefunctPlayer && duration) {
      this.props.onSetAdDuration(duration);
    }
  }

  @autobind
  handleAdIsSkippableChange(event) {
    const isSkippable = event.detail.value;
    if (isFunction(this.props.onAdIsSkippableChange) && !this.isDefunctPlayer) {
      this.props.onAdIsSkippableChange(isSkippable);
    }
  }

  @autobind
  handleAdIsSkipAllowedNowChange(event) {
    const isSkipAllowedNow = event.detail.value;
    if (
      isFunction(this.props.onAdIsSkipAllowedNowChange) &&
      !this.isDefunctPlayer
    ) {
      this.props.onAdIsSkipAllowedNowChange(isSkipAllowedNow);
    }
  }

  @autobind
  handleLoadedData() {
    if (this.props.waitForDataLoadedToSeek) {
      this.seek({ value: this.props.time });
    }
  }

  // @autobind
  // handleVideoPlayerStalled() {
  //     if (this.videoPlayer.duration() > 0 && this.videoPlayer.currentTime() < 1) {

  //     }
  // }

  @autobind
  attachPlayerEvents() {
    if (this.playerEventsAttached) {
      return;
    }

    this.playerEventsAttached = true;

    this.attachBufferEvents(this.videoPlayer, {
      stateKey: "bufferPrimary",
      useCallback: true,
    });

    // A video's playback rate is reset whenever the source changes, so reset
    // the playback rate when we get a new source.
    this.attachSinglePlayerEvent("canplay", () => {
      this.videoPlayer.playbackRate(this.props.playbackRate);
    });

    // Call the `handleReady` callback when the video is ready to start
    // playing.
    this.attachSinglePlayerEvent("canplay", this.handleCanplay);
    this.attachSinglePlayerEvent("loadeddata", this.handleLoadedData);

    // this.attachSinglePlayerEvent("stalled", this.handlePrimaryNodeStalled);
    this.attachSinglePlayerEvent("error", (rootError) => {
      const currentSrc = this.videoPlayer.currentSrc();
      const error = rootError.target.player.error();
      sendErrorReport(new Error(error.message), { videoSource: currentSrc });
    });

    // Update `state.readyState` for all these events
    [
      "loadedalldata",
      "loadeddata",
      "loadedmetadata",
      "pause",
      "play",
      "stalled",
      "timeupdate",
      "waiting",
    ].forEach((event) => {
      this.attachSinglePlayerEvent(event, this.updateReadyState);
    });

    // Capture video events to determine if we're really playing
    this.attachSinglePlayerEvent("playing", () => {
      this.setState({ actuallyPlaying: true });
      this.props.onPlaying();
    });
    this.attachSinglePlayerEvent("pause", () =>
      this.setState({ actuallyPlaying: false }),
    );
    this.attachSinglePlayerEvent("waiting", () =>
      this.setState({ actuallyPlaying: false }),
    );

    this.attachSinglePlayerEvent("loadstart", this.props.onLoadStart);
    this.attachSinglePlayerEvent("timeupdate", this.handleTimeUpdate);

    this.attachSinglePlayerEvent("seeked", this.handleVideoSeeked);
    this.attachSinglePlayerEvent("seeking", this.handleVideoSeeking);
    this.attachSinglePlayerEvent("play", this.handlePlay);
    this.attachSinglePlayerEvent("pause", this.handlePause);
    this.attachSinglePlayerEvent("stalled", this.doAutoplayCheck);
    this.attachSinglePlayerEvent("durationchange", this.handleDurationChange);
    this.attachSinglePlayerEvent("ended", this.onEnded);
  }

  @autobind
  updateReadyState() {
    this.setState({
      readyState: this.videoPlayer.readyState(),
    });
  }

  // Call the callback with the new time value.
  @autobind
  dispatchTimeUpdate(time) {
    if (
      !this.isDefunctPlayer &&
      !this.isBusy &&
      isFunction(this.props.onTimeUpdate)
    ) {
      this.props.onTimeUpdate(time);
    }
  }

  @autobind
  dispatchAdTimeUpdate(time) {
    if (
      !this.isDefunctPlayer &&
      !this.isBusy &&
      isFunction(this.props.onTimeUpdate)
    ) {
      this.props.onTimeUpdate(time, { isAdTime: true });
    }
  }

  @autobind
  handlePlay() {
    if (isFunction(this.props.onPlay) && !this.videoPlayer.paused()) {
      this.props.onPlay();
    }
  }

  @autobind
  handlePause() {
    this.props.onPause && this.props.onPause();
  }

  @autobind
  handlePrimaryNodeStalled() {
    // Determine how much time is left in the primary buffer
    const bufferedTimeRemaining = findBufferedTimeRemaining(
      this.state.bufferPrimary,
      this.currentPrimaryTime,
    );

    if (bufferedTimeRemaining < 500) {
      // The primary node is stalled. We should stop doing
      // anything until it unstalls.
      this.setState({
        primaryNodeStalled: true,
      });
    }
  }

  detachSinglePlayerEvent(eventNameToRemove) {
    const newPlayerEvents = [];

    this.playerEvents.forEach((eventDefinition) => {
      const { event, callback, target } = eventDefinition;

      if (event === eventNameToRemove) {
        target.off(event, callback);
      } else {
        newPlayerEvents.push(eventDefinition);
      }
    });

    this.playerEvents = newPlayerEvents;
  }

  @autobind
  detachPlayerEvents() {
    // If we're using a persistent video player and the promise chain to
    // start playback has not yet been resolved, then destroy it. If it
    // exists, it's waiting for playback to start and by destroying it,
    // that playback will never start and will hang the player.
    this.constructor.videoPlayerControlPromiseChain = null;

    if (!this.playerEvents.length) {
      return;
    }

    while (this.playerEvents.length) {
      const { event, callback, target } = this.playerEvents.pop();

      target.off(event, callback);
    }

    this.playerEventsAttached = false;

    this.detachThreeScene();
  }

  get primaryMediaNode() {
    return this.primaryMediaNodeRef.current;
  }

  get isVideoOnly() {
    return this.props.forceMute;
  }

  @autobind
  handleTouchStart(evt) {
    if (this.props.is360) {
      evt.stopPropagation();
    }
  }

  // Return if the primary node has stalled
  get isStalled() {
    return this.state.primaryNodeStalled;
  }

  get isBusy() {
    const invalidReadyState =
      !this.props.adPlaying &&
      this.state.readyState < VideoReadyState.HAVE_FUTURE_DATA;
    return (
      this.props.playing &&
      (invalidReadyState || (this.state.seeking && !this.props.adPlaying))
    );
  }

  get isActuallyPlaying() {
    return this.state.actuallyPlaying;
  }

  get isPlaying() {
    return this.props.playing;
  }

  get isVideoAtEnd() {
    if (!this.videoPlayer) {
      return false;
    }

    return (
      Math.abs(this.videoPlayer.currentTime() - this.videoPlayer.duration()) <
      0.25
    );
  }

  render() {
    const className = classnames(
      "video-wrap verse-chapter-content",
      styles.root,
      {
        "verse-chapter-content--paused": this.props.userInitiatedPause,
        [styles.hide]: this.props.hide,
        [styles.is360]: this.props.is360,
      },
    );

    const dataAttributesHash = (() => {
      if (!this.props.dataAttributes) {
        return {};
      }

      return this.props.dataAttributes.split(/\s+/).reduce(
        (memo, dataAttribute) => ({
          ...memo,
          [dataAttribute]: true,
        }),
        {},
      );
    })();

    return (
      <div
        aria-busy={this.isBusy}
        aria-hidden={!this.props.active}
        aria-label="Video"
        className={className}
        data-playing={this.isActuallyPlaying}
        data-stalled={this.isStalled}
        data-video-id={this.props.videoId}
        onTouchStart={this.handleTouchStart}
        ref={this.containerRef}
        role="application"
        {...dataAttributesHash}
      />
    );
  }
}
